
import { useAuth } from '@/contexts/AuthContext';
import { EventFilters, ViewMode } from '@/lib/types';
import { useEventLoading } from './calendar/useEventLoading';
import { useCalendarSize } from './calendar/useCalendarSize';
import { useCalendarNavigation } from './calendar/useCalendarNavigation';
import { useEventFiltering } from './calendar/useEventFiltering';
import { useCallback, useState, useRef, useEffect } from 'react';

// Function to get the stored view mode from localStorage
const getStoredViewMode = (): ViewMode => {
  try {
    const storedMode = localStorage.getItem('calendarViewMode');
    return storedMode === 'list' ? 'list' : 'calendar';
  } catch (error) {
    // In case of any localStorage errors (private browsing, etc.)
    console.error("Could not access localStorage:", error);
    return 'calendar';
  }
};

// Function to save the view mode to localStorage
const saveViewMode = (mode: ViewMode): void => {
  try {
    localStorage.setItem('calendarViewMode', mode);
  } catch (error) {
    console.error("Could not save to localStorage:", error);
  }
};

// Ellenőrizzük, hogy van-e mentett szűrő a localStorage-ban
const hasSavedFilters = (): boolean => {
  try {
    return localStorage.getItem('medihub-filter-settings') !== null;
  } catch (error) {
    console.error("Nem sikerült ellenőrizni a mentett szűrőket:", error);
    return false;
  }
};

export function useCalendar() {
  const { token } = useAuth();
  const initialized = useRef(false);
  const hasAppliedFilter = useRef(false);

  // Initialize viewMode from localStorage
  const [viewMode, setViewMode] = useState<ViewMode>(getStoredViewMode());
  const [isFilterActive, setIsFilterActive] = useState(hasSavedFilters());

  const { allEvents } = useEventLoading(token);
  const { calendarContainerRef, daysToShow, cellWidth } = useCalendarSize();

  const {
    currentDate,
    days,
    calendarStartDate,
    calendarEndDate,
    navigatePrevious,
    navigateNext,
    navigateToToday,
    handleDateFromFilterChange
  } = useCalendarNavigation(daysToShow, [], allEvents, initialized.current);

  const {
    filteredEvents,
    totalFilteredEvents,
    handleFilterChange
  } = useEventFiltering(allEvents, calendarStartDate, calendarEndDate);

  // Wrap the setViewMode in a function that also saves to localStorage
  const toggleViewMode = useCallback(() => {
    setViewMode(prev => {
      const newMode = prev === 'calendar' ? 'list' : 'calendar';
      saveViewMode(newMode);
      return newMode;
    });
  }, []);

  const handleFilterChangeWithDateNavigation = useCallback((newFilters: EventFilters) => {
    const hasActiveFilters =
      (newFilters.type && newFilters.type.length > 0) ||
      (newFilters.professions && newFilters.professions.length > 0) ||
      (newFilters.location && newFilters.location.length > 0) ||
      newFilters.hasCreditPoints ||
      !!newFilters.search;

    setIsFilterActive(hasActiveFilters);

    if (hasActiveFilters && !hasAppliedFilter.current) {
      setViewMode('list');
      saveViewMode('list');
      hasAppliedFilter.current = true;
    } else if (!hasActiveFilters && hasAppliedFilter.current) {
      hasAppliedFilter.current = false;
    }

    handleFilterChange(newFilters);

    if (newFilters.dateRange?.startDate) {
      handleDateFromFilterChange(newFilters.dateRange.startDate);
    }
  }, [handleFilterChange, handleDateFromFilterChange]);

  // Inicializáljuk az isFilterActive állapotot a localStorage alapján
  useEffect(() => {
    if (hasSavedFilters()) {
      hasAppliedFilter.current = true;
    }
  }, []);

  useEffect(() => {
    if (!initialized.current && allEvents.length > 0) {
      initialized.current = true;
    }
  }, [allEvents.length]);

  return {
    initialized: initialized.current,
    currentDate,
    days,
    daysToShow,
    calendarContainerRef,
    cellWidth,
    filteredEvents,
    totalFilteredEvents,
    viewMode,
    toggleViewMode,
    handleFilterChange: handleFilterChangeWithDateNavigation,
    navigatePrevious,
    navigateNext,
    navigateToToday,
    isFilterActive
  };
}
